/**
 * Analyze tab — Energy Optimization & Reports tools (BIM "Energy
 * Optimization" + "Reports & Schedules").
 *
 * - Energy Settings: dialog for building type, setpoints, utility rates.
 * - Create Energy Model: derives thermal Analytical Spaces & Surfaces from
 *   the physical model (energyEngine.buildEnergyModelFromElements).
 * - Heating & Cooling Loads: envelope + internal-gain peak load calc.
 * - Perform Energy Analysis: annual energy/carbon/cost (Insight-style).
 * - Export gbXML: serialize the energy model for DOE2/eQUEST/Robot.
 */

import { BaseTool } from '../BaseTool';
import { bimKernel } from '../../core';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import {
    buildEnergyModelFromElements,
    calculateLoads,
    calculateAnnualEnergy,
    generateGbXML,
    downloadTextFile,
    EnergyModelResult,
} from './energyEngine';
import i18n from '../../i18n';

/** Current elements snapshot as a plain array. */
function allElements(): any[] {
    return Array.from(bimKernel.getProject().elements.values());
}

/** Ensure an energy model exists; build it on demand like BIM does. */
function ensureEnergyModel(): EnergyModelResult {
    const store = useAnalysisStore.getState();
    const model = buildEnergyModelFromElements(allElements());
    if (model.spaces.length === 0) {
        return model;
    }
    store.setEnergyModel({
        spaceCount: model.spaces.length,
        surfaceCount: model.surfaces.length,
        totalArea: model.totalArea,
        totalVolume: model.totalVolume,
        createdOn: model.createdOn,
    });
    return model;
}

export class EnergySettingsTool extends BaseTool {
    id = 'ANALYZE_ENERGY_SETTINGS';
    name = 'Energy Settings';
    cursor = 'default';

    onActivate(): void {
        this.context?.appStore.openManageDialog('analyzeEnergySettings');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class CreateEnergyModelTool extends BaseTool {
    id = 'ANALYZE_ENERGY_MODEL';
    name = 'Create Energy Model';
    cursor = 'default';

    onActivate(): void {
        const model = buildEnergyModelFromElements(allElements());
        if (model.spaces.length === 0) {
            this.notify(i18n.t('tools.notify.noThermalSpacesFoundDrawSpaces'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        useAnalysisStore.getState().setEnergyModel({
            spaceCount: model.spaces.length,
            surfaceCount: model.surfaces.length,
            totalArea: model.totalArea,
            totalVolume: model.totalVolume,
            createdOn: model.createdOn,
        });
        useAnalysisStore.getState().bumpSpaces();
        this.notify(
            i18n.t('tools.notify.energyModelCreated', {
                spaces: model.spaces.length,
                surfaces: model.surfaces.length,
                area: model.totalArea.toFixed(1)
            }),
            'success'
        );
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class HeatingCoolingLoadsTool extends BaseTool {
    id = 'ANALYZE_LOADS';
    name = 'Heating and Cooling Loads';
    cursor = 'default';

    onActivate(): void {
        const model = ensureEnergyModel();
        if (model.spaces.length === 0) {
            this.notify(i18n.t('tools.notify.createAnEnergyModelFirstSpaces'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        const settings = useAnalysisStore.getState().energySettings;
        const result = calculateLoads(model, settings);
        const store = useAnalysisStore.getState();
        store.setLastLoads(result);
        store.setReport({
            title: 'Heating & Cooling Loads',
            generatedOn: Date.now(),
            lines: [
                `Building type: ${settings.buildingType}`,
                `Design temperatures: ${settings.winterDesignTempC} °C / ${settings.summerDesignTempC} °C`,
                ``,
                `Total heating load: ${(result.totalHeatingW / 1000).toFixed(1)} kW  (${result.heatingPerM2.toFixed(1)} W/m²)`,
                `Total cooling load: ${(result.totalCoolingW / 1000).toFixed(1)} kW  (${result.coolingPerM2.toFixed(1)} W/m²)`,
                ``,
                `Per-space breakdown (W):`,
                ...result.perSpace.map(
                    s => `  ${s.spaceName.padEnd(22)} H ${(s.heatingLoadW / 1000).toFixed(2)} kW · C ${(s.coolingLoadW / 1000).toFixed(2)} kW`
                ),
            ],
        });
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class PerformEnergyAnalysisTool extends BaseTool {
    id = 'ANALYZE_PERFORM_ENERGY';
    name = 'Perform Energy Analysis';
    cursor = 'default';

    onActivate(): void {
        const model = ensureEnergyModel();
        if (model.spaces.length === 0) {
            this.notify(i18n.t('tools.notify.createAnEnergyModelFirstSpaces2'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        const settings = useAnalysisStore.getState().energySettings;
        const loads = calculateLoads(model, settings);
        const annual = calculateAnnualEnergy(model, loads, settings);
        const store = useAnalysisStore.getState();
        store.setLastEnergy(annual);
        store.setReport({
            title: 'Annual Energy Analysis (Insight)',
            generatedOn: Date.now(),
            lines: [
                `Conditioned area: ${model.totalArea.toFixed(1)} m²`,
                `Annual consumption: ${annual.totalKwh.toFixed(0)} kWh  (${annual.kwhPerM2Year.toFixed(1)} kWh/m²·yr)`,
                `Annual cost: ${annual.totalCost.toFixed(0)} · Carbon: ${(annual.totalCarbonKg / 1000).toFixed(1)} tCO2e`,
                `Heating EUI: ${annual.heatingEUIKwh.toFixed(1)} kWh/m² · Cooling EUI: ${annual.coolingEUIKwh.toFixed(1)} kWh/m²`,
                `Cheapest month: ${annual.bestCostMonth} · Most expensive: ${annual.worstCostMonth}`,
                ``,
                `Monthly (kWh): H / C / L / E`,
                ...annual.monthly.map(
                    m => `  ${m.month}  ${(m.heatingKwh).toFixed(0).padStart(6)} / ${(m.coolingKwh).toFixed(0).padStart(6)} / ${(m.lightingKwh).toFixed(0).padStart(5)} / ${(m.equipmentKwh).toFixed(0).padStart(6)}`
                ),
            ],
        });
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class ExportGbXMLTool extends BaseTool {
    id = 'ANALYZE_EXPORT_GBXML';
    name = 'Export gbXML';
    cursor = 'default';

    onActivate(): void {
        const model = ensureEnergyModel();
        if (model.spaces.length === 0) {
            this.notify(i18n.t('tools.notify.noEnergyModelToExportCreate'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        const settings = useAnalysisStore.getState().energySettings;
        const projectName = bimKernel.getProject().name || 'YapProject';
        const xml = generateGbXML(model, settings, projectName);
        downloadTextFile(`${projectName.replace(/\s+/g, '_')}.gbxml`, xml);
        this.notify(i18n.t('tools.notify.gbxmlExported', { spaces: model.spaces.length, surfaces: model.surfaces.length }), 'success');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
